import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Clock, Mail, Phone } from "lucide-react";
import { CONTACT, REQUIREMENTS } from "../../lib/data";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "", mobile: "", email: "", city: "",
  requirement: "", loan_amount: "", details: "", message: "", consent: false,
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">{label}</span>
    {children}
  </label>
);

const inputCls =
  "mt-2 w-full border-b border-gray-300 bg-transparent py-3 text-[15px] text-navy placeholder:text-gray-400 outline-none transition-colors duration-300 focus:border-gold";

export const EnquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const apply = (req) => setForm((f) => ({ ...f, requirement: req }));
    const applyAmount = (amt) => setForm((f) => ({ ...f, loan_amount: amt }));
    const stored = sessionStorage.getItem("lendx_requirement");
    if (stored) {
      apply(stored);
      sessionStorage.removeItem("lendx_requirement");
    }
    const storedAmt = sessionStorage.getItem("lendx_amount");
    if (storedAmt) {
      applyAmount(storedAmt);
      sessionStorage.removeItem("lendx_amount");
    }
    const onEvent = (e) => apply(e.detail);
    const onAmount = (e) => applyAmount(e.detail);
    window.addEventListener("lendx:requirement", onEvent);
    window.addEventListener("lendx:amount", onAmount);
    return () => {
      window.removeEventListener("lendx:requirement", onEvent);
      window.removeEventListener("lendx:amount", onAmount);
    };
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent statement to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Enquiry submitted. A LendX expert will contact you shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" className="relative overflow-hidden bg-white py-24 md:py-32" data-testid="enquiry-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-gold/8 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-5 lg:gap-20">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-deep">Lead Form</p>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-navy md:text-5xl" data-testid="enquiry-heading">
              Talk to a Finance Expert
            </h2>
            <p className="mt-5 text-base leading-relaxed text-gray-600 md:text-lg">
              Share your requirement and our advisory team will assess your financing options and reach out with the right approach.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 space-y-5">
            <a href={CONTACT.phoneHref} data-testid="enquiry-call-link" className="flex items-center gap-4 text-gray-600 transition-colors hover:text-navy">
              <span className="flex h-10 w-10 items-center justify-center rounded-md border border-gold-line bg-gold-tint text-gold-deep"><Phone size={16} /></span>
              {CONTACT.phone}
            </a>
            <a href={CONTACT.landlineHref} data-testid="enquiry-landline-link" className="flex items-center gap-4 text-gray-600 transition-colors hover:text-navy">
              <span className="flex h-10 w-10 items-center justify-center rounded-md border border-gold-line bg-gold-tint text-gold-deep"><Phone size={16} /></span>
              {CONTACT.landline} <span className="text-xs text-gray-400">(Landline)</span>
            </a>
            <a href={CONTACT.emailHref} data-testid="enquiry-email-link" className="flex items-center gap-4 text-gray-600 transition-colors hover:text-navy">
              <span className="flex h-10 w-10 items-center justify-center rounded-md border border-gold-line bg-gold-tint text-gold-deep"><Mail size={16} /></span>
              {CONTACT.email}
            </a>
            <div className="flex items-center gap-4 text-gray-600">
              <span className="flex h-10 w-10 items-center justify-center rounded-md border border-gold-line bg-gold-tint text-gold-deep"><Clock size={16} /></span>
              {CONTACT.hours}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-[0_16px_48px_rgba(16,20,24,0.08)] md:p-12">
            {done ? (
              <div className="flex flex-col items-center py-14 text-center" data-testid="enquiry-success">
                <CheckCircle2 size={52} className="text-gold-deep" />
                <h3 className="mt-6 font-heading text-2xl font-bold text-navy md:text-3xl">Thank You for Reaching Out</h3>
                <p className="mt-4 max-w-md text-gray-600">
                  Your enquiry has been received. A LendX finance expert will review your requirement and contact you shortly.
                </p>
                <button
                  onClick={() => { setForm(initial); setDone(false); }}
                  data-testid="enquiry-again-btn"
                  className="mt-8 rounded-full border border-gray-300 px-6 py-3 text-sm font-medium text-gray-800 transition-colors duration-300 hover:border-orange hover:text-orange-deep"
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="enquiry-form" className="grid gap-7 md:grid-cols-2">
                <Field label="Full Name *">
                  <input required value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} data-testid="enquiry-input-name" />
                </Field>
                <Field label="Mobile Number *">
                  <input required value={form.mobile} onChange={set("mobile")} placeholder="+91" className={inputCls} data-testid="enquiry-input-mobile" />
                </Field>
                <Field label="Email Address *">
                  <input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} data-testid="enquiry-input-email" />
                </Field>
                <Field label="City *">
                  <input required value={form.city} onChange={set("city")} placeholder="Mumbai" className={inputCls} data-testid="enquiry-input-city" />
                </Field>
                <Field label="Type of Requirement *">
                  <select required value={form.requirement} onChange={set("requirement")} className={`${inputCls} cursor-pointer bg-white`} data-testid="enquiry-select-requirement">
                    <option value="" disabled>Select requirement</option>
                    {REQUIREMENTS.map((r) => (
                      <option key={r} value={r} className="bg-white">{r}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Required Loan Amount *">
                  <input required value={form.loan_amount} onChange={set("loan_amount")} placeholder="e.g. ₹ 2.5 Cr" className={inputCls} data-testid="enquiry-input-amount" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Property / Business Details">
                    <input value={form.details} onChange={set("details")} placeholder="Brief details of the property or business" className={inputCls} data-testid="enquiry-input-details" />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Message">
                    <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Anything else we should know?" className={`${inputCls} resize-none`} data-testid="enquiry-input-message" />
                  </Field>
                </div>
                <label className="flex cursor-pointer items-start gap-3 md:col-span-2" data-testid="enquiry-consent">
                  <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-4 w-4 accent-[#D4AF37]" data-testid="enquiry-consent-checkbox" />
                  <span className="text-xs leading-relaxed text-gray-500">
                    By submitting this form, you agree to be contacted by LendX regarding your financing requirements.
                  </span>
                </label>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="enquiry-submit-btn"
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-bold text-inkx transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                  >
                    {submitting ? "Submitting…" : "Submit Enquiry"}
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
